import { liveReload } from '@shared/services/realtime.service';
import { PageLoaderComponent, PageLoad } from '@app/shared/components/page-loader/page-loader.component';
import { FilterChipsComponent, FilterChip } from '@app/shared/components/filter-bar/filter-chips.component';
import { SearchPillComponent } from '@app/shared/components/filter-bar/search-pill.component';
import { PageTitleComponent } from '@app/shared/components/page-title/page-title.component';
import { Component, OnInit, inject, ViewChild } from '@angular/core';

import { Router } from '@angular/router';
// Material
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
// Services & Models
import { RolesService } from '@shared/services/entities/roles.service';
import { ModulesService } from '@shared/services/modules.service';
import { PermissionsService } from '@shared/services/permissions.service';
import {
  Role,
  CreateRolePayload,
  UpdateRolePayload,
  AccessPermissionsMap,
} from '@shared/models/entities/roles.model';
import { ModuleModel } from '@shared/models/entities/module.model';
import { Permission } from '@shared/models/entities/permission.model';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
import { SimpleExcelService } from '@shared/services/simple-excel.service';
//Utils
import { sortAlphabetically } from '@shared/utils/sort.utils';
import { RoleFormDialogComponent } from './role-form-dialog/role-form-dialog.component';

import { ListSelection, ListView } from '@app/shared/utils/list-view';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { SelectionBarComponent } from '@app/shared/components/selection-bar/selection-bar.component';
@Component({
  selector: 'app-roles',
  standalone: true,
  imports: [ SelectionBarComponent, MatButtonToggleModule, PageLoaderComponent, SearchPillComponent, FilterChipsComponent, PageTitleComponent, 
    MatTableModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatExpansionModule,
    MatDialogModule
],
  templateUrl: './roles.component.html',
  styleUrl: './roles.component.scss',
})
export class RolesComponent implements OnInit {
  /** Vista tabla / tarjetas y selección con checkbox (misma que en catálogos). */
  readonly listView = new ListView('roles', 'table');
  readonly sel = new ListSelection<any>(() => (this.dataSource?.filteredData ?? []) as any[]);
  private readonly excelSvc = inject(SimpleExcelService);

  /** Exporta a Excel la selección. */
  async exportExcel(rows: any[]): Promise<void> {
    if (!rows?.length) return;
    await this.excelSvc.simpleXLSX({
      fileName: 'Roles',
      sheets: [{
        sheetName: 'Roles'.slice(0, 31), header: [], lineBreak: 0,
        columns: [
          { columnKey: 'name', columnName: 'Rol' },
          { columnKey: 'description', columnName: 'Descripción' },
          { columnKey: '_modules', columnName: 'Módulos' },
          { columnKey: '_perms', columnName: 'Permisos' },
        ],
        data: rows.map((r: any) => ({ ...r, _modules: this.getModuleLabels(r).join(', '), _perms: this.getPermissionsCount(r) })),
      }],
    } as any);
  }

  private modulesSvcTitle = inject(ModulesService);
  /** Título del módulo = etiqueta visible del catálogo de Módulos. */
  title = this.modulesSvcTitle.moduleTitle('/roles', 'Roles');


  private readonly rolesService         = inject(RolesService);
  private readonly modulesService       = inject(ModulesService);
  private readonly permissionsService   = inject(PermissionsService);
  private readonly customMessageService = inject(CustomMessageService);
  private readonly router               = inject(Router);
  private readonly simpleExcel          = inject(SimpleExcelService);
  private readonly dialog               = inject(MatDialog);

  // ─── Estado ────────────────────────────────────────────────────────────────
  loading = this.rolesService.loading;

  // ─── Catálogos ─────────────────────────────────────────────────────────────
  availableModules:     ModuleModel[] = [];
  availablePermissions: Permission[]  = [];

  // ─── Tabla ─────────────────────────────────────────────────────────────────
  columns    = ['select', 'name', 'description', 'modules', 'permissions', 'actions'];
  dataSource = new MatTableDataSource<Role>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  /** Pantalla de carga del módulo: bloquea hasta terminar la carga inicial. */
  readonly pageLoad = new PageLoad();
  /** Tiempo real: recarga cuando otro usuario cambia estos módulos (WebSocket). */
  private readonly live = liveReload(['roles', 'modules', 'permissions'], () => this.pageLoad.ready && this.pageLoad.refresh(this.loadAll()));

  ngOnInit(): void {
    void this.init();
  }

  /** Carga inicial (también la usa "Reintentar"). */
  init(): Promise<boolean> {
    return this.pageLoad.run(() => this.initialLoad());
  }

  private async initialLoad(): Promise<void> {
    await this.loadAll();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Carga de datos
  // ─────────────────────────────────────────────────────────────────────────────

  async loadAll(): Promise<void> {
    await Promise.all([
      this.loadRoles(),
      this.loadModules(),
      this.loadPermissions(),
    ]);
  }

  async loadRoles(): Promise<void> {
    try {
      await this.rolesService.search({ searchType: 'default', condition: { deleted: false } });
      this.dataSource.data = sortAlphabetically(this.rolesService.listData(), 'name');
      this.dataSource.paginator = this.paginator;
    } catch {
      await this.customMessageService.open({
        title: 'Error', type: 'error',
        message: 'No se pudieron cargar los roles.', dismissBtn: true,
      });
    }
  }

  async loadModules(): Promise<void> {
    try {
      await this.modulesService.search({ searchType: 'default', condition: { deleted: false, active: true } });
      this.availableModules = sortAlphabetically(this.modulesService.listData(), 'label');
    } catch {
      console.error('No se pudieron cargar los módulos');
    }
  }

  async loadPermissions(): Promise<void> {
    try {
      await this.permissionsService.search({ searchType: 'default', condition: { deleted: false, active: true } });
      this.availablePermissions = sortAlphabetically(this.permissionsService.listData(), 'name');
    } catch {
      console.error('No se pudieron cargar los permisos');
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Alta / edición: en diálogo compartido (FormDialogShell) — título y
  // acciones fijos, contenido scrolleable — en vez del panel inline.
  // ─────────────────────────────────────────────────────────────────────────────

  openCreate(): void {
    this.openFormDialog();
  }

  openEdit(role: Role): void {
    this.openFormDialog(role);
  }

  private openFormDialog(role?: Role): void {
    const ref = this.dialog.open(RoleFormDialogComponent, {
      data: {
        role,
        modules:     this.availableModules,
        permissions: this.availablePermissions,
      },
      width: '760px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      autoFocus: 'first-tabbable',
    });
    ref.afterClosed().subscribe(saved => {
      if (saved) this.loadRoles();
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // CRUD
  // ─────────────────────────────────────────────────────────────────────────────

  async deleteRole(role: Role): Promise<void> {
    const confirm = await this.customMessageService.open({
      title: 'Cuidado', type: 'warning',
      message: `¿Seguro que quieres eliminar el rol <b>${role.name}</b>?`,
      acceptBtn: true, dismissBtn: true,
    });
    if (!confirm) return;

    try {
      await this.rolesService.softDeleteRole(role.id);
      await this.loadRoles();
    } catch {
      await this.customMessageService.open({
        title: 'Error', type: 'error',
        message: 'No se pudo eliminar el rol.', dismissBtn: true,
      });
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Búsqueda
  // ─────────────────────────────────────────────────────────────────────────────

  onSearch(event: Event): void {
    const val = (event.target as HTMLInputElement).value;
    this.dataSource.filter = val.trim().toLowerCase();
    this.dataSource.paginator?.firstPage();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Helpers para la tabla
  // ─────────────────────────────────────────────────────────────────────────────

  /** Etiquetas de módulos con acceso (para chips en la tabla). */
  getModuleLabels(role: Role): string[] {
    if (!role.navAccessTree?.length) return [];
    return role.navAccessTree
      .map(id => this.availableModules.find(m => m.id === id)?.label ?? id)
      .filter(Boolean);
  }

  /** Total de permisos asignados sumando todos los módulos. */
  getPermissionsCount(role: Role): number {
    if (!role.accessPermissions) return 0;
    return Object.values(role.accessPermissions)
      .reduce((sum, list) => sum + (list?.length ?? 0), 0);
  }

  /** Descarga la tabla de roles como Excel.
   *
   * Formato tipo esquema: la PRIMERA fila del rol lleva la información
   * básica (rol, descripción, total) junto con el primer módulo y su
   * primer permiso; de ahí cada permiso baja un renglón, y el nombre del
   * módulo solo se escribe cuando cambia. Ejemplo:
   *
   *   ADMINISTRADOR | Rol maestro | 42 | Auditoría  | Crear
   *                 |             |    |            | Borrar
   *                 |             |    | Contratos  | Leer
   */
  async downloadExcel(): Promise<void> {
    const roles: Role[] = this.dataSource.data ?? [];
    if (roles.length === 0) {
      await this.customMessageService.open({ title: 'Información', type: 'neutral', message: 'No hay roles que descargar.', duration: 2000 });
      return;
    }

    // Mapa id → nombre de permiso. Se trae la lista COMPLETA (incluye
    // inactivos y con comparación tolerante de tipos): un rol puede
    // referenciar un permiso que ya no está activo, y sin esto salía el
    // id crudo en el Excel en lugar del nombre.
    const permNameById = new Map<string, string>();
    for (const perm of this.availablePermissions) {
      permNameById.set(String(perm.id), perm.name ?? String(perm.id));
    }
    try {
      const fullResponse: any = await this.permissionsService.search({ searchType: 'default', condition: { deleted: false } });
      for (const perm of (fullResponse?.data ?? [])) {
        if (perm?.id && !permNameById.has(String(perm.id))) {
          permNameById.set(String(perm.id), perm.name ?? String(perm.id));
        }
      }
    } catch { /* si falla, se usa lo ya cargado */ }

    const rows: any[] = [];
    for (const role of roles) {
      let firstRowOfRole = true;
      const access = role.accessPermissions ?? {};
      const modules = role.navAccessTree ?? [];

      for (const moduleId of modules) {
        const moduleLabel = this.availableModules.find(m => String(m.id) === String(moduleId))?.label ?? moduleId;
        const permNames = (access[moduleId] ?? [])
          .map(id => permNameById.get(String(id)) ?? String(id));
        if (permNames.length === 0) permNames.push('Sin permisos');

        let firstRowOfModule = true;
        for (const perm of permNames) {
          rows.push({
            name:             firstRowOfRole ? (role.name ?? '') : '',
            description:      firstRowOfRole ? (role.description ?? '') : '',
            permissionsCount: firstRowOfRole ? this.getPermissionsCount(role) : '',
            module:           firstRowOfModule ? moduleLabel : '',
            permission:       perm,
          });
          firstRowOfRole = false;
          firstRowOfModule = false;
        }
      }

      // Rol sin módulos: al menos su fila básica
      if (firstRowOfRole) {
        rows.push({
          name: role.name ?? '',
          description: role.description ?? '',
          permissionsCount: this.getPermissionsCount(role),
          module: 'Sin módulos',
          permission: '',
        });
      }

      // Renglón en blanco entre roles, para que se distingan
      rows.push({ name: '', description: '', permissionsCount: '', module: '', permission: '' });
    }
    rows.pop(); // sin separador después del último

    await this.simpleExcel.simpleXLSX({
      fileName: 'Roles-Medtronic-Surgical',
      sheets: [{
        sheetName: 'Roles',
        header: [],
        lineBreak: 0,
        columns: [
          { columnKey: 'name',             columnName: 'Rol' },
          { columnKey: 'description',      columnName: 'Descripción' },
          { columnKey: 'permissionsCount', columnName: 'Total de permisos' },
          { columnKey: 'module',           columnName: 'Módulos' },
          { columnKey: 'permission',       columnName: 'Permisos por módulo' },
        ],
        data: rows,
      }],
    });
  }

  navigateTo(route: string): void {
    this.router.navigate([route]);
  }
}